import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import ProductCard from "../../components/ProductCard";
import { Package, Heart, User, CreditCard, MapPin, Calendar, Plus, Trash2, Check, Gift, Bell, CheckCheck, Wallet, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ClientReferrals from "./ClientReferrals";

export default function ClientDashboard() {
    return (
        <>
            <Navbar />
            <section className="pt-28 pb-20 bg-white min-h-[80vh]">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <Sidebar />
                    <div className="lg:col-span-10 space-y-8">
                        <TopBar />
                        <Routes>
                            <Route index element={<Orders />} />
                            <Route path="encomendas" element={<Orders />} />
                            <Route path="favoritos" element={<Favorites />} />
                            <Route path="assinaturas" element={<Subscriptions />} />
                            <Route path="indique-e-ganhe" element={<ClientReferrals />} />
                            <Route path="moradas" element={<Addresses />} />
                            <Route path="perfil" element={<Profile />} />
                        </Routes>
                    </div>
                </div>
            </section>
            <Footer />
        </>
    );
}

function TopBar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <header className="flex items-center justify-between bg-white rounded-md border border-gold-soft/30 px-6 py-4 shadow-sm">
            <div>
                <span className="label-overline text-wine">A minha conta</span>
                <h1 className="font-heading text-2xl text-wine-dark leading-none mt-1">
                    Olá, {user?.name?.split(" ")[0]}
                </h1>
            </div>
            <div className="flex items-center gap-4">
                <span className="text-xs text-wine-dark/60 hidden md:flex items-center gap-2">
                    <Calendar className="h-4 w-4" strokeWidth={1.5} />
                    {new Date().toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                </span>
                <NotificationBell />
                <button onClick={async () => { await logout(); nav("/"); }} className="btn-wine-outline text-xs px-5 py-2.5">
                    Sair
                </button>
            </div>
        </header>
    );
}

const NOTIF_ICON = {
    reward_credit: Gift, wallet_used: Wallet, credit_expiring: Calendar,
    reward_reversed: RotateCcw, wallet_refunded: Wallet, referral_qualified: Gift,
};

function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const load = () => api.get("/notifications").then((r) => {
        setItems(r.data?.items || []);
        setUnread(r.data?.unread || 0);
    }).catch(() => {});
    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);
    const markRead = async (id) => {
        await api.post(`/notifications/${id}/read`).catch(() => {});
        setItems((xs) => xs.map((n) => n.id === id ? { ...n, read: true } : n));
        setUnread((u) => Math.max(u - 1, 0));
    };
    const markAll = async () => {
        await api.post("/notifications/read-all").catch(() => {});
        setItems((xs) => xs.map((n) => ({ ...n, read: true })));
        setUnread(0);
    };
    return (
        <div className="relative">
            <button onClick={() => { setOpen((o) => !o); if (!open) load(); }} className="relative p-2.5 rounded-md border border-gold-soft/40 text-wine-dark hover:border-wine" data-testid="notif-bell">
                <Bell className="h-4 w-4" strokeWidth={1.5} />
                {unread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-wine text-cream text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">{unread > 9 ? "9+" : unread}</span>
                )}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gold-soft/40 rounded-md shadow-xl z-50" data-testid="notif-panel">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-wine-dark/10 sticky top-0 bg-white">
                            <span className="label-overline text-wine">Notificações</span>
                            {unread > 0 && <button onClick={markAll} className="text-[10px] label-overline text-wine hover:text-gold-deep inline-flex items-center gap-1" data-testid="notif-mark-all"><CheckCheck className="h-3.5 w-3.5" /> Marcar todas</button>}
                        </div>
                        {items.length === 0 && <p className="text-center text-sm text-wine-dark/50 py-8">Sem notificações.</p>}
                        {items.map((n) => {
                            const Icon = NOTIF_ICON[n.type] || Bell;
                            return (
                                <button key={n.id} onClick={() => markRead(n.id)} className={`w-full text-left px-4 py-3 border-b border-wine-dark/5 hover:bg-cream flex gap-3 ${n.read ? "opacity-60" : ""}`} data-testid={`notif-item-${n.id}`}>
                                    <Icon className="h-4 w-4 text-wine mt-0.5 shrink-0" strokeWidth={1.5} />
                                    <div className="min-w-0">
                                        <p className="text-sm text-wine-dark font-medium">{n.title}{!n.read && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-wine align-middle" />}</p>
                                        <p className="text-xs text-wine-dark/65 mt-0.5">{n.message}</p>
                                        <p className="text-[10px] text-wine-dark/40 mt-1">{new Date(n.created_at).toLocaleString("pt-PT")}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

function Sidebar() {
    const { user } = useAuth();
    const links = [
        { to: "/conta/encomendas", label: "Encomendas", icon: Package },
        { to: "/conta/favoritos", label: "Favoritos", icon: Heart },
        { to: "/conta/assinaturas", label: "Clube", icon: CreditCard },
        { to: "/conta/indique-e-ganhe", label: "Indique e Ganhe", icon: Gift },
        { to: "/conta/moradas", label: "Moradas", icon: MapPin },
        { to: "/conta/perfil", label: "Perfil", icon: User },
    ];
    return (
        <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-wine rounded-md flex items-center justify-center text-cream font-heading text-xl">
                        {user?.name?.charAt(0).toUpperCase() || "C"}
                    </div>
                    <div className="min-w-0">
                        <span className="label-overline text-wine">Cliente</span>
                        <p className="font-heading text-base text-wine-dark leading-tight mt-0.5 truncate">{user?.name}</p>
                    </div>
                </div>
            </div>
            <nav className="bg-white rounded-md border border-gold-soft/30 p-2 shadow-sm">
                {links.map(({ to, label, icon: Icon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-xs transition-all ${
                                isActive ? "bg-wine text-cream font-semibold" : "text-wine-dark hover:bg-wine/5"
                            }`
                        }
                        data-testid={`client-nav-${label}`}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        <span className="flex-1 uppercase tracking-wider">{label}</span>
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}

/* ---------- ORDERS ---------- */
function Orders() {
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get("/orders/me").then((r) => setOrders(r.data)); }, []);
    return (
        <Section title="As minhas encomendas" subtitle={`${orders.length} total`}>
            {orders.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="Ainda sem encomendas"
                    cta={<Link to="/marketplace" className="btn-wine mt-6">Descobrir produtos</Link>}
                />
            ) : (
                <div className="space-y-4">
                    {orders.map((o) => (
                        <div key={o.id} className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm" data-testid={`order-${o.id}`}>
                            <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
                                <div>
                                    <span className="font-mono text-xs text-wine-dark/60">#{o.id.slice(0, 8)}</span>
                                    <p className="text-sm text-wine-dark/60 mt-1">
                                        {new Date(o.created_at).toLocaleString("pt-PT")}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className={`label-overline px-2 py-1 rounded text-[10px] ${
                                        o.status === "paid" ? "bg-vineyard/10 text-vineyard" : "bg-gold-soft/20 text-gold-deep"
                                    }`}>
                                        {o.status === "paid" ? "Paga" : o.status}
                                    </span>
                                    {o.shipment_ids && o.shipment_ids.length > 0 && (
                                        <Link to={`/tracking/${o.shipment_ids[0].slice(0, 12)}`} className="text-xs text-wine mt-2 label-overline inline-flex items-center gap-1 hover:text-gold-deep" data-testid={`tracking-link-${o.id}`}>
                                            Rastrear envio{o.shipment_ids.length > 1 ? "s" : ""} →
                                        </Link>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-1">
                                {o.items.map((it, i) => (
                                    <p key={`${it.product_id || "i"}-${i}`} className="text-sm text-wine-dark/80">{it.quantity}× {it.name}</p>
                                ))}
                            </div>
                            <div className="hairline my-4" />
                            <div className="flex justify-between">
                                <span className="text-sm text-wine-dark/60">Total</span>
                                <span className="font-heading text-xl text-wine-dark">€{o.total.toFixed(2)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

/* ---------- FAVORITES ---------- */
function Favorites() {
    const [favs, setFavs] = useState([]);
    useEffect(() => { api.get("/favorites").then((r) => setFavs(r.data)); }, []);
    return (
        <Section title="Favoritos" subtitle="Produtos guardados">
            {favs.length === 0 ? (
                <EmptyState
                    icon={Heart}
                    title="Ainda sem favoritos"
                    cta={<Link to="/marketplace" className="btn-wine mt-6">Explorar marketplace</Link>}
                />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {favs.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
            )}
        </Section>
    );
}

/* ---------- SUBSCRIPTIONS ---------- */
function Subscriptions() {
    const [subs, setSubs] = useState([]);
    const [plans, setPlans] = useState([]);
    const [changing, setChanging] = useState(null);

    async function load() {
        const [s, p] = await Promise.all([api.get("/subscriptions/me"), api.get("/subscription-plans")]);
        setSubs(s.data);
        setPlans(p.data);
    }
    useEffect(() => { load(); }, []);

    async function cancel(id) {
        if (!window.confirm("Cancelar assinatura?")) return;
        await api.post(`/subscriptions/${id}/cancel`);
        toast.success("Assinatura cancelada");
        load();
    }
    async function pause(id) {
        await api.post(`/subscriptions/${id}/pause`);
        toast.success("Assinatura pausada");
        load();
    }
    async function changePlan(subId, planId) {
        await api.post(`/subscriptions/${subId}/change-plan`, { plan_id: planId, origin_url: window.location.origin });
        toast.success("Plano atualizado");
        setChanging(null);
        load();
    }

    return (
        <Section title="Clube Caseirinho" subtitle="As suas assinaturas">
            {subs.length === 0 ? (
                <EmptyState
                    icon={CreditCard}
                    title="Sem assinaturas ativas"
                    cta={<Link to="/clube" className="btn-wine mt-6">Conhecer planos</Link>}
                />
            ) : (
                <div className="space-y-4">
                    {subs.map((s) => (
                        <div key={s.id} className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm" data-testid={`sub-${s.id}`}>
                            <div className="flex justify-between items-start flex-wrap gap-4">
                                <div>
                                    <span className="label-overline text-gold-deep">Plano atual</span>
                                    <h3 className="font-heading text-3xl text-wine-dark mt-2">{s.plan_name}</h3>
                                    <p className="text-sm text-wine-dark/60 mt-1">€{s.price.toFixed(2)} /mês</p>
                                    <span className={`label-overline mt-3 inline-block px-2 py-1 rounded text-[10px] ${
                                        s.status === "active" ? "bg-vineyard/10 text-vineyard" :
                                        s.status === "paused" ? "bg-wine-dark/5 text-wine-dark/60" :
                                        s.status === "cancelled" ? "bg-destructive/10 text-destructive" :
                                        "bg-gold-soft/20 text-gold-deep"
                                    }`}>
                                        {s.status}
                                    </span>
                                </div>
                                {(s.status === "active" || s.status === "paused") && (
                                    <div className="flex flex-wrap gap-2">
                                        <button onClick={() => setChanging(changing === s.id ? null : s.id)} className="btn-wine-outline text-xs px-4 py-2">
                                            Mudar plano
                                        </button>
                                        {s.status === "active" && (
                                            <button onClick={() => pause(s.id)} className="btn-wine-outline text-xs px-4 py-2" data-testid={`pause-${s.id}`}>
                                                Pausar
                                            </button>
                                        )}
                                        <button onClick={() => cancel(s.id)} className="text-xs text-destructive hover:underline px-2 py-2" data-testid={`cancel-${s.id}`}>
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>

                            {changing === s.id && (
                                <div className="mt-6 pt-6 border-t border-wine-dark/10 grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {plans.map((p) => {
                                        const isCurrent = p.id === s.plan_id;
                                        return (
                                            <button
                                                key={p.id}
                                                onClick={() => !isCurrent && changePlan(s.id, p.id)}
                                                disabled={isCurrent}
                                                className={`p-4 border rounded-md text-left transition-all ${
                                                    isCurrent
                                                        ? "border-vineyard bg-vineyard/5 cursor-default"
                                                        : "border-wine-dark/20 hover:border-wine"
                                                }`}
                                                data-testid={`change-plan-${p.id}`}
                                            >
                                                <p className="font-heading text-lg text-wine-dark">{p.name}</p>
                                                <p className="text-xs text-wine-dark/60 mt-1">€{p.price.toFixed(2)}/mês</p>
                                                {isCurrent && (
                                                    <span className="label-overline text-vineyard mt-2 inline-flex items-center gap-1 text-[10px]">
                                                        <Check className="h-3 w-3" /> Atual
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {s.next_renewal && (
                                <p className="mt-4 text-xs text-wine-dark/60">
                                    Próxima renovação: {new Date(s.next_renewal).toLocaleDateString("pt-PT")}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

/* ---------- ADDRESSES ---------- */
function Addresses() {
    const [list, setList] = useState([]);
    const [show, setShow] = useState(false);
    const empty = { label: "Casa", full_name: "", street: "", city: "", postal_code: "", country: "Portugal", phone: "", is_default: false };
    const [form, setForm] = useState(empty);

    async function load() {
        const { data } = await api.get("/addresses");
        setList(data);
    }
    useEffect(() => { load(); }, []);

    async function submit(e) {
        e.preventDefault();
        try {
            await api.post("/addresses", form);
            toast.success("Morada adicionada");
            setShow(false);
            setForm(empty);
            load();
        } catch { toast.error("Erro ao guardar morada"); }
    }
    async function del(id) {
        if (!window.confirm("Eliminar esta morada?")) return;
        await api.delete(`/addresses/${id}`);
        load();
    }

    return (
        <Section title="Moradas" subtitle="As suas moradas de envio">
            <div className="flex justify-end mb-4">
                <button onClick={() => setShow(!show)} className="btn-wine inline-flex items-center gap-2" data-testid="new-address">
                    <Plus className="h-4 w-4" /> Nova morada
                </button>
            </div>
            {show && (
                <form onSubmit={submit} className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <SmallField label="Etiqueta" value={form.label} set={(v) => setForm({ ...form, label: v })} />
                    <SmallField label="Nome completo" value={form.full_name} set={(v) => setForm({ ...form, full_name: v })} />
                    <SmallField label="Morada" value={form.street} set={(v) => setForm({ ...form, street: v })} full />
                    <SmallField label="Cidade" value={form.city} set={(v) => setForm({ ...form, city: v })} />
                    <SmallField label="Código postal" value={form.postal_code} set={(v) => setForm({ ...form, postal_code: v })} />
                    <SmallField label="Telefone" value={form.phone} set={(v) => setForm({ ...form, phone: v })} required={false} />
                    <label className="flex items-center gap-2 md:col-span-2">
                        <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-wine" />
                        <span className="text-sm">Definir como padrão</span>
                    </label>
                    <div className="md:col-span-2 flex gap-3 justify-end">
                        <button type="button" onClick={() => setShow(false)} className="btn-wine-outline">Cancelar</button>
                        <button className="btn-wine">Guardar morada</button>
                    </div>
                </form>
            )}
            {list.length === 0 && !show ? (
                <EmptyState icon={MapPin} title="Sem moradas guardadas" cta={null} />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map((a) => (
                        <div key={a.id} className="bg-white border border-gold-soft/30 rounded-md p-5 shadow-sm relative" data-testid={`address-${a.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="label-overline text-gold-deep">{a.label}{a.is_default && " · Padrão"}</span>
                                    <p className="font-heading text-lg text-wine-dark mt-2">{a.full_name}</p>
                                    <p className="text-sm text-wine-dark/70 mt-2">{a.street}</p>
                                    <p className="text-sm text-wine-dark/70">{a.postal_code} {a.city}</p>
                                    <p className="text-sm text-wine-dark/70">{a.country}</p>
                                    {a.phone && <p className="text-xs text-wine-dark/60 mt-2">{a.phone}</p>}
                                </div>
                                <button onClick={() => del(a.id)} className="text-wine-dark/60 hover:text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

/* ---------- PROFILE ---------- */
function Profile() {
    const { user } = useAuth();
    return (
        <Section title="Perfil" subtitle="Os seus dados">
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm max-w-md space-y-4">
                <Row label="Nome" value={user?.name} />
                <Row label="Email" value={user?.email} />
                <Row label="Tipo de conta" value={user?.role} />
                <div className="hairline" />
                <Link to="/forgot-password" className="label-overline text-wine hover:text-gold-deep">
                    Alterar palavra-passe →
                </Link>
            </div>
        </Section>
    );
}

/* ---------- SHARED ---------- */
function Section({ title, subtitle, children }) {
    return (
        <div>
            <div className="mb-6">
                <h2 className="font-heading text-4xl text-wine-dark">{title}</h2>
                {subtitle && <p className="text-sm text-wine-dark/60 mt-2">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function EmptyState({ icon: Icon, title, cta }) {
    return (
        <div className="bg-white border border-gold-soft/30 rounded-md p-16 text-center shadow-sm">
            <Icon className="h-12 w-12 text-wine/30 mx-auto" strokeWidth={1.5} />
            <p className="mt-4 text-wine-dark/70">{title}</p>
            {cta}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <span className="label-overline text-wine-dark/60">{label}</span>
            <p className="text-wine-dark mt-1">{value}</p>
        </div>
    );
}

function SmallField({ label, value, set, full, required = true }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="label-overline text-wine-dark">{label}</span>
            <input
                value={value}
                onChange={(e) => set(e.target.value)}
                required={required}
                className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-3 py-2.5 text-sm focus:border-wine"
            />
        </label>
    );
}
